import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type PlaygroundTokens = {
  brandHue: number;
  brandSaturation: number;
  brandLightness: number;
  radiusScale: number;
  surfaceContrast: number;
  fontSizeScale: number;
};

const DEFAULT_TOKENS: PlaygroundTokens = {
  brandHue: 271,
  brandSaturation: 91,
  brandLightness: 65,
  radiusScale: 1,
  surfaceContrast: 1,
  fontSizeScale: 1,
};

const MANAGED_CSS_VARS = [
  '--brand-hue',
  '--brand-saturation',
  '--brand-lightness',
  '--brand',
  '--brand-hover',
  '--primary',
  '--secondary',
  '--gradient-start',
  '--gradient-end',
  '--radius-scale',
  '--radius',
  '--surface-contrast',
  '--font-size-scale',
  '--muted',
  '--card',
];

const tokenSwatches = [
  { name: 'Background', variable: '--background' },
  { name: 'Foreground', variable: '--foreground' },
  { name: 'Primary', variable: '--primary' },
  { name: 'Secondary', variable: '--secondary' },
  { name: 'Muted', variable: '--muted' },
  { name: 'Accent', variable: '--accent' },
  { name: 'Destructive', variable: '--destructive' },
  { name: 'Card', variable: '--card' },
];

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const ControlSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}> = ({ label, value, min, max, step = 1, onChange }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <span className="text-xs text-muted-foreground">{value}</span>
    </div>
    <Input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={event => onChange(Number(event.target.value))}
    />
  </div>
);

const DesignSystemShowcase: React.FC = () => {
  const [tokens, setTokens] = React.useState<PlaygroundTokens>(DEFAULT_TOKENS);
  const [isDarkPreview, setIsDarkPreview] = React.useState(() =>
    document.documentElement.classList.contains('dark')
  );

  const applyTokens = React.useCallback(
    (nextTokens: PlaygroundTokens, darkModeEnabled: boolean) => {
      const root = document.documentElement;
      const secondaryHue = (nextTokens.brandHue + 68) % 360;
      const secondaryLightness = clamp(nextTokens.brandLightness - 1, 50, 72);
      const mutedLightness = clamp(
        96 - (nextTokens.surfaceContrast - 1) * 18,
        82,
        98
      );
      const darkMutedLightness = clamp(
        17 - (nextTokens.surfaceContrast - 1) * 8,
        9,
        25
      );

      root.style.setProperty('--brand-hue', String(nextTokens.brandHue));
      root.style.setProperty(
        '--brand-saturation',
        `${nextTokens.brandSaturation}%`
      );
      root.style.setProperty(
        '--brand-lightness',
        `${nextTokens.brandLightness}%`
      );
      root.style.setProperty(
        '--brand',
        `${nextTokens.brandHue} ${nextTokens.brandSaturation}% ${nextTokens.brandLightness}%`
      );
      root.style.setProperty(
        '--brand-hover',
        `${nextTokens.brandHue} ${clamp(nextTokens.brandSaturation - 8, 60, 95)}% ${clamp(nextTokens.brandLightness - 6, 42, 72)}%`
      );
      root.style.setProperty(
        '--primary',
        `${nextTokens.brandHue} ${nextTokens.brandSaturation}% ${nextTokens.brandLightness}%`
      );
      root.style.setProperty(
        '--secondary',
        `${secondaryHue} ${nextTokens.brandSaturation}% ${secondaryLightness}%`
      );
      root.style.setProperty(
        '--gradient-start',
        `${nextTokens.brandHue} ${nextTokens.brandSaturation}% ${nextTokens.brandLightness}%`
      );
      root.style.setProperty(
        '--gradient-end',
        `${secondaryHue} ${nextTokens.brandSaturation}% ${secondaryLightness}%`
      );
      root.style.setProperty('--radius-scale', String(nextTokens.radiusScale));
      root.style.setProperty(
        '--radius',
        `${(0.75 * nextTokens.radiusScale).toFixed(3)}rem`
      );
      root.style.setProperty(
        '--surface-contrast',
        String(nextTokens.surfaceContrast)
      );
      root.style.setProperty(
        '--font-size-scale',
        String(nextTokens.fontSizeScale)
      );

      if (darkModeEnabled) {
        root.style.setProperty('--muted', `215 27.9% ${darkMutedLightness}%`);
        root.style.setProperty(
          '--card',
          `224 71% ${clamp(4 - (nextTokens.surfaceContrast - 1) * 2.8, 2, 10)}%`
        );
      } else {
        root.style.setProperty('--muted', `220 14.3% ${mutedLightness}%`);
        root.style.setProperty(
          '--card',
          `0 0% ${clamp(100 - (nextTokens.surfaceContrast - 1) * 7, 92, 100)}%`
        );
      }
    },
    []
  );

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', isDarkPreview);
    applyTokens(tokens, isDarkPreview);
  }, [applyTokens, isDarkPreview, tokens]);

  React.useEffect(() => {
    return () => {
      const root = document.documentElement;
      MANAGED_CSS_VARS.forEach(variable => root.style.removeProperty(variable));
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8 p-1 sm:p-2">
      <header className="space-y-2">
        <Badge variant="outline">Hidden page: /design-system</Badge>
        <h1 className="text-3xl font-semibold tracking-tight">
          Design System Lab
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Use this page to validate tokens and shared primitives before applying
          updates to production screens.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Live Token Controls</CardTitle>
          <CardDescription>
            Adjust theme variables in real time. Reset to restore system
            defaults.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <ControlSlider
              label="Brand hue"
              value={tokens.brandHue}
              min={0}
              max={360}
              onChange={value =>
                setTokens(previous => ({ ...previous, brandHue: value }))
              }
            />
            <ControlSlider
              label="Brand saturation"
              value={tokens.brandSaturation}
              min={50}
              max={100}
              onChange={value =>
                setTokens(previous => ({ ...previous, brandSaturation: value }))
              }
            />
            <ControlSlider
              label="Brand lightness"
              value={tokens.brandLightness}
              min={45}
              max={80}
              onChange={value =>
                setTokens(previous => ({ ...previous, brandLightness: value }))
              }
            />
            <ControlSlider
              label="Surface contrast"
              value={tokens.surfaceContrast}
              min={0.8}
              max={1.4}
              step={0.05}
              onChange={value =>
                setTokens(previous => ({ ...previous, surfaceContrast: value }))
              }
            />
            <ControlSlider
              label="Radius scale"
              value={tokens.radiusScale}
              min={0.6}
              max={1.8}
              step={0.05}
              onChange={value =>
                setTokens(previous => ({ ...previous, radiusScale: value }))
              }
            />
            <ControlSlider
              label="Typography scale"
              value={tokens.fontSizeScale}
              min={0.9}
              max={1.2}
              step={0.01}
              onChange={value =>
                setTokens(previous => ({ ...previous, fontSizeScale: value }))
              }
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-md border p-2">
              <Label htmlFor="theme-preview">Dark preview</Label>
              <Switch
                id="theme-preview"
                checked={isDarkPreview}
                onCheckedChange={setIsDarkPreview}
              />
            </div>
            <Button onClick={() => setTokens(DEFAULT_TOKENS)} variant="outline">
              Reset variables
            </Button>
          </div>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">Color Tokens</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tokenSwatches.map(swatch => (
            <div key={swatch.variable} className="rounded-xl border p-3">
              <div
                className="h-14 rounded-md border"
                style={
                  {
                    backgroundColor: `hsl(var(${swatch.variable}))`,
                  } as React.CSSProperties
                }
              />
              <p className="mt-2 text-sm font-medium">{swatch.name}</p>
              <p className="text-xs text-muted-foreground">{swatch.variable}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Buttons & Badges</CardTitle>
            <CardDescription>Core interaction variants</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <Button>Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="tertiary">Tertiary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="gradient">Gradient</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
              <Badge variant="gradient">Gradient</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Form Controls</CardTitle>
            <CardDescription>
              Unified input and selection primitives
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sample-input">Input</Label>
              <Input id="sample-input" placeholder="Type here..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sample-textarea">Textarea</Label>
              <Textarea id="sample-textarea" placeholder="Longer message..." />
            </div>
            <div className="space-y-2">
              <Label>Select</Label>
              <Select defaultValue="default">
                <SelectTrigger>
                  <SelectValue placeholder="Choose style density" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compact">Compact</SelectItem>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="comfortable">Comfortable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Dialog & Feedback</CardTitle>
            <CardDescription>
              Modal interactions and content hierarchy
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open example dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm style update</DialogTitle>
                  <DialogDescription>
                    This previews how semantic components render in modal
                    context.
                  </DialogDescription>
                </DialogHeader>
                <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  Buttons, focus rings, and surface colors all resolve from
                  tokens.
                </div>
                <DialogFooter>
                  <Button variant="outline">Cancel</Button>
                  <Button>Apply</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tabs</CardTitle>
            <CardDescription>
              Information grouping and navigation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="components" className="w-full">
              <TabsList className="w-full">
                <TabsTrigger value="components">Components</TabsTrigger>
                <TabsTrigger value="tokens">Tokens</TabsTrigger>
                <TabsTrigger value="layout">Layout</TabsTrigger>
              </TabsList>
              <TabsContent
                value="components"
                className="text-sm text-muted-foreground"
              >
                Shared primitives keep behavior and styling consistent across
                pages.
              </TabsContent>
              <TabsContent
                value="tokens"
                className="text-sm text-muted-foreground"
              >
                Semantic tokens prevent hardcoded color drift and simplify
                theming.
              </TabsContent>
              <TabsContent
                value="layout"
                className="text-sm text-muted-foreground"
              >
                Layouts should compose cards, spacing rhythm, and typography
                scale.
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Data Table</CardTitle>
          <CardDescription>
            Tabular styles tied to semantic borders and text
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>
              Design token impact across component states.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Variable</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">--primary</TableCell>
                <TableCell>
                  {tokens.brandHue} / {tokens.brandSaturation}% /{' '}
                  {tokens.brandLightness}%
                </TableCell>
                <TableCell>Main action color</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">--radius</TableCell>
                <TableCell>
                  {(0.75 * tokens.radiusScale).toFixed(2)}rem
                </TableCell>
                <TableCell>Component corner rounding</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">--font-size-scale</TableCell>
                <TableCell>{tokens.fontSizeScale.toFixed(2)}</TableCell>
                <TableCell>Global typography scaling</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DesignSystemShowcase;
